import { Dispatch, SetStateAction, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const safeDecode = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

function useQueryState<T extends string>(
    key: string,
    defaultValue?: T
): [T, Dispatch<SetStateAction<T>>] {
    const [searchParams, setSearchParams] = useSearchParams();

    const param = searchParams.get(key);
    const value = (param ? safeDecode(param) : (defaultValue ?? '')) as T;

    const update: Dispatch<SetStateAction<T>> = useCallback((newValue) => {
        const params = new URLSearchParams(window.location.search);
        const currentParam = params.get(key);
        const currentValue = (currentParam ? safeDecode(currentParam) : (defaultValue ?? '')) as T;

        const resolved =
            typeof newValue === "function"
                ? (newValue as (prev: T) => T)(currentValue)
                : newValue;

        if (resolved) {
            params.set(key, encodeURIComponent(resolved));
        } else {
            params.delete(key);
        }

        setSearchParams(params, { replace: true });
    }, [key, defaultValue, setSearchParams]);

    return [value, update];
}

export default useQueryState;
